import { DisplayObject, Filter, Container } from '@tencent/alloy-renderer';
import AnimFiltersFactory from './anim-filters-factory';
import { IAnimTarget, IAnimFilter, IAnimFilterUpdate, IAnimFilterOptions } from '../anim-interface';
import { AnimFilters } from '../anim-constants';
import { shouldDisableAnimCacheAndFilter } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-utils';
import { AnimType } from '@tencent/slide-sdk-model';
import { Action } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-constants';

export default class AnimFiltersManager {
    public static instance: AnimFiltersManager;
    public static getInstance(): AnimFiltersManager {
        if (!this.instance) {
            this.instance = new AnimFiltersManager();
        }
        return this.instance;
    }

    /**
     * 动画开始时，重建滤镜，或重置滤镜状态
     * @param animType 滤镜动画类型
     * @param animTarget 动画目标元素
     */
    public handleAnimFiltersStart(
        animFilterType: AnimFilters,
        animTarget: IAnimTarget,
        filterOptions: Partial<IAnimFilterOptions>,
    ): void {
        if (shouldDisableAnimCacheAndFilter(animTarget)) {
            return;
        }
        const eleRenderCom = this.getEleRenderComponent(animTarget);
        if (!eleRenderCom) return;
        let animFilter = this.getAnimFilterByType(eleRenderCom, animFilterType) as IAnimFilter;
        if (!animFilter) {
            animFilter = AnimFiltersFactory.createAnimFilter(animFilterType, filterOptions) as IAnimFilter;
            eleRenderCom.addFilter(animFilter);
        } else {
            animFilter.rate = 0;
            animFilter.direction = filterOptions.direction;
            animFilter.action = filterOptions.action;
        }
    }

    /**
     * 动画结束时，设置滤镜状态
     * @param animType 滤镜动画类型
     * @param animTarget 动画目标元素
     * @param action 动画入场类型
     * @param reset 恢复成初始状态，进度(rate)重置为 0，不传保留最终状态
     */
    public handleAnimFiltersComplete(
        animFilterType: AnimFilters,
        animTarget: IAnimTarget,
        action: Action,
        reset?: boolean,
    ): void {
        const eleRenderCom = this.getEleRenderComponent(animTarget);
        if (!eleRenderCom) return;
        const animFilter = this.getAnimFilterByType(eleRenderCom, animFilterType) as IAnimFilter;
        if (!animFilter) return;
        animFilter.action = action;
        animFilter.rate = reset ? 0 : 1;
    }

    /**
     * 获取元素上的动画滤镜
     * @param animTarget 动画目标元素
     * @param animType 动画类型
     * @returns 动画滤镜
     */
    public getAnimFilter(animTarget: IAnimTarget, animType: string): IAnimFilter | undefined {
        const animFilterType = this.getAnimFilterType(animType);
        if (!animFilterType) return;
        const eleRenderCom = this.getEleRenderComponent(animTarget);
        if (!eleRenderCom) return;
        return this.getAnimFilterByType(eleRenderCom, animFilterType) as IAnimFilter;
    }

    /**
     * 回退动画时，滤镜动画元素重置为初始态
     */
    public resetAnimFilters(animType: string, presetClass: string, animTarget: IAnimTarget) {
        const animFilterType = this.getAnimFilterType(animType);
        if (!animFilterType) return;
        this.handleAnimFiltersComplete(animFilterType, animTarget, presetClass as Action, true);
    }

    /**
     * 动画更新中，设置滤镜状态
     * @param animType 滤镜动画类型
     * @param animTarget 动画目标元素
     */
    public handleAnimFiltersUpdate({
        animTarget,
        animFilterType,
        animIns,
        duration,
    }: IAnimFilterUpdate): void {
        const eleRenderCom = this.getEleRenderComponent(animTarget);
        if (!eleRenderCom) return;
        const animFilter = this.getAnimFilterByType(eleRenderCom, animFilterType) as IAnimFilter;
        if (!animFilter) return;
        animFilter.rate = animIns.instance.time() / duration;
    }

    /**
     * 获取滤镜动画类型
     * @param animType 动画类型
     * @returns AnimFilters
     */
    public getAnimFilterType(animType: string): AnimFilters | null {
        const animFilterType = AnimFiltersFactory.animTypeFilterMap.get(animType as AnimType);
        if (!animFilterType) return null;
        return animFilterType;
    }

    /**
     * 获取动画目标元素的renderComponent
     * @param animTarget 动画目标元素
     * @returns 元素渲染组件
     */
    private getEleRenderComponent(animTarget: IAnimTarget): Container {
        // @ts-ignore
        const eleRenderComponent = animTarget.children[0]?.children[0];
        return eleRenderComponent;
    }

    /**
     * 获取对应type的滤镜
     * @param eleRenderCom 元素渲染组件
     * @param type 滤镜类型
     * @returns 滤镜
     */
    private getAnimFilterByType(eleRenderCom: DisplayObject, filterType: string): Filter | undefined {
        return eleRenderCom.filters?.find((filter: Filter) => filter?.type === filterType);
    }
}
